import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { BaseSheet } from "@/components/common/BaseSheet";
import {
  GenieDocumentBuilderSheet,
  GenieDocumentFormatGallery,
  GenieDocumentSuiteBanner,
  GenieDocumentSuiteReviewSheet,
  SavedGenieDocumentCard,
} from "@/components/genie-documents";
import { GenieKnowledgeHeader, GenieKnowledgeTabs } from "@/components/knowledge-base/genie";
import { Button } from "@/components/ui/button";
import { useAgent } from "@/hooks/agent";
import { useDocuments } from "@/hooks/documents/useDocumentFetch";
import { useDeleteDocument } from "@/hooks/documents/useDocumentMutations";
import { useGenerateGenieDocumentSuite } from "@/hooks/genie-documents/useGenerateGenieDocumentSuite";
import { useProposeGenieDocumentSuite } from "@/hooks/genie-documents/useProposeGenieDocumentSuite";
import {
  toGenieDocumentEntries,
  type GenieDocumentEntry,
} from "@/hooks/genie-documents/useGenieDocuments";
import { usePublishGenieDocument } from "@/hooks/genie-documents/usePublishGenieDocument";
import { useKnowledgeBrain } from "@/hooks/knowledge/useKnowledgeBrain";
import { geniePageUrl, isPageLive, pageBlockedReason } from "@/pages/UserHub/GeniePages/geniePageStatus";
import { genieDocumentBrand } from "@/services/genie-documents/genieDocumentBrand";
import { isGenieDocument, type GenieDocumentSuiteProposal } from "@/types/genie-documents/genie-document";
import { toast } from "sonner";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

/**
 * Branded documents for one Genie — `/genie-hub/knowledge-base/:genieId/documents`.
 *
 * It lives in the knowledge base because that is where the raw material is: every
 * document here is generated from this Genie's sources, and the only place it can
 * be published to is this Genie's public page. The Genie detail page links here
 * rather than carrying a second builder.
 */
const GenieDocumentsPage = () => {
  const { genieId } = useParams<{ genieId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: genie, isLoading: isLoadingGenie } = useAgent(genieId!);

  // Everything attached to this Genie, then split: sources feed the generator,
  // generated documents are the library. A generated document must never be
  // offered as a source — that is how a Genie ends up citing itself.
  const { data: attached = [], isLoading: isLoadingAttached } = useDocuments("my-documents", {
    agentId: genieId,
  });

  const sources = useMemo(() => attached.filter((doc) => !isGenieDocument(doc)), [attached]);
  const documents = useMemo(() => toGenieDocumentEntries(attached), [attached]);

  // Topic clusters let the owner say "everything about memberships" instead of
  // ticking nine filenames. Same brain the Sources tab reads.
  const { clusters } = useKnowledgeBrain({ userId: user?.id, documents: sources, openQuestions: [] });

  const deleteDocument = useDeleteDocument();
  const { setPublished, pendingId } = usePublishGenieDocument();

  const proposeSuite = useProposeGenieDocumentSuite();
  const generateSuite = useGenerateGenieDocumentSuite();

  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [builderFormatKey, setBuilderFormatKey] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<GenieDocumentEntry | null>(null);
  const [entryToDelete, setEntryToDelete] = useState<GenieDocumentEntry | null>(null);

  // The document suite: planner proposals, held client-side until the owner
  // approves a subset and the swarm drafts them.
  const [suiteProposals, setSuiteProposals] = useState<GenieDocumentSuiteProposal[] | null>(null);

  // Opened from the knowledge brain's "create a document" with a topic in hand.
  const clusterId = searchParams.get("cluster");
  const shouldAutoOpen = searchParams.get("create") === "1";
  const autoOpened = useRef(false);

  // Arriving from the Sources tab means the owner already asked to build
  // something — open the builder rather than making them click again. Once only,
  // so closing it doesn't bounce straight back open.
  useEffect(() => {
    if (shouldAutoOpen && !autoOpened.current) {
      autoOpened.current = true;
      setIsBuilderOpen(true);
    }
  }, [shouldAutoOpen]);

  const openBuilder = (formatKey: string | null) => {
    setEditingEntry(null);
    setBuilderFormatKey(formatKey);
    setIsBuilderOpen(true);
  };

  const closeBuilder = () => {
    setIsBuilderOpen(false);
    setEditingEntry(null);
    setBuilderFormatKey(null);
    if (clusterId || shouldAutoOpen) {
      searchParams.delete("cluster");
      searchParams.delete("create");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const openForEdit = (entry: GenieDocumentEntry) => {
    setEditingEntry(entry);
    setBuilderFormatKey(entry.stored.formatKey ?? null);
    setIsBuilderOpen(true);
  };

  const confirmDelete = () => {
    if (!entryToDelete) return;
    deleteDocument.mutate(entryToDelete.row.id, { onSettled: () => setEntryToDelete(null) });
  };

  const handleProposeSuite = async () => {
    if (!genie) return;
    try {
      const result = await proposeSuite.mutateAsync(genie.id);
      if (result.suite.length === 0) {
        toast.message("Your Genie couldn't find enough to propose documents from yet.");
        return;
      }
      setSuiteProposals(result.suite);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Couldn't propose a suite");
    }
  };

  const handleGenerateSuite = async (approved: GenieDocumentSuiteProposal[]) => {
    if (!genie) return;
    try {
      const result = await generateSuite.mutateAsync({
        agentId: genie.id,
        suite: approved.map((p) => ({
          title: p.title,
          template: p.template,
          documentIds: p.documentIds,
        })),
      });

      setSuiteProposals(null);

      const ungrounded = result.created.filter((doc) => !doc.grounded);
      if (result.created.length > 0) {
        toast.success(
          `Created ${result.created.length} draft${result.created.length === 1 ? "" : "s"} — review and publish below.`,
        );
      }
      if (ungrounded.length > 0) {
        toast.message(
          `${ungrounded.length} draft${ungrounded.length === 1 ? "" : "s"} flagged to double-check before publishing.`,
        );
      }
      if (result.failed.length > 0) {
        toast.error(`${result.failed.length} document${result.failed.length === 1 ? "" : "s"} couldn't be created.`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Couldn't create the suite");
    }
  };

  if (isLoadingGenie || isLoadingAttached) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9FAFC]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-genie-primary" />
          <p className="text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (!genie) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9FAFC]">
        <div className="text-center">
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Genie Not Found</h2>
          <p className="mb-4 text-gray-600">
            The genie you're looking for doesn't exist or you don't have access to it.
          </p>
          <Button onClick={() => navigate("/genie-hub/knowledge-base")}>Back to Knowledge Base</Button>
        </div>
      </div>
    );
  }

  const brand = genieDocumentBrand(genie);
  const pageLive = isPageLive(genie);
  const publishBlockedReason = pageBlockedReason(genie);
  const pageUrl = geniePageUrl(genie);

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <GenieDocumentBuilderSheet
        isOpen={isBuilderOpen}
        onClose={closeBuilder}
        agent={genie}
        sources={sources}
        clusters={clusters}
        initialFormatKey={builderFormatKey}
        initialClusterId={clusterId}
        editingEntry={editingEntry}
      />

      <GenieDocumentSuiteReviewSheet
        isOpen={suiteProposals !== null}
        onClose={() => setSuiteProposals(null)}
        proposals={suiteProposals ?? []}
        isGenerating={generateSuite.isPending}
        onGenerate={(approved) => void handleGenerateSuite(approved)}
      />

      <BaseSheet
        isOpen={!!entryToDelete}
        onClose={() => !deleteDocument.isPending && setEntryToDelete(null)}
        title="Delete this document"
        description="The document is removed everywhere, including your Genie Page. Your knowledge sources are untouched."
        size="md"
        footer={
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setEntryToDelete(null)}
              disabled={deleteDocument.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deleteDocument.isPending}
              className="bg-gradient-to-r from-genie-primary to-genie-secondary text-white"
            >
              {deleteDocument.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete document"
              )}
            </Button>
          </div>
        }
      >
        <div className="px-6 py-6 sm:px-8">
          <p className="text-sm text-gray-600">
            Delete{" "}
            <span className="font-semibold text-gray-900">{entryToDelete?.stored.title}</span>? This
            cannot be undone.
          </p>
        </div>
      </BaseSheet>

      <div className="container mx-auto max-w-[1400px] px-4 py-6 md:py-8">
        <GenieKnowledgeHeader
          genie={genie}
          documentCount={documents.length}
          lastUpdated={null}
          onBack={() => navigate("/genie-hub/knowledge-base")}
          onAddKnowledge={() => openBuilder(null)}
          metaLabel={`${documents.length} document${documents.length === 1 ? "" : "s"} · ${sources.length} source${sources.length === 1 ? "" : "s"} to draw from`}
          primaryAction={{ label: "Create a document", onClick: () => openBuilder(null) }}
        />

        <GenieKnowledgeTabs genieId={genie.id} documentCount={documents.length} />

        {/* ── The suite — one click, a full set of guides ──────────────────── */}
        <GenieDocumentSuiteBanner
          sourceCount={sources.length}
          onPropose={() => void handleProposeSuite()}
          isProposing={proposeSuite.isPending}
        />

        {/* ── What you can make ─────────────────────────────────────────────── */}
        <section className="mb-8">
          <div className="mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Make something from this knowledge</h2>
            <p className="text-sm text-gray-600">
              Every format is written only from the sources you pick, in {brand.name}&apos;s brand,
              ready to print, send, or publish to your Genie Page.
            </p>
          </div>
          <GenieDocumentFormatGallery onSelect={(format) => openBuilder(format.key)} />
        </section>

        {/* ── What you've made ──────────────────────────────────────────────── */}
        <section>
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Your documents</h2>
              <p className="text-sm text-gray-600">
                New documents go live as guides on{" "}
                {pageLive && pageUrl ? (
                  <a
                    href={pageUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="font-medium text-genie-primary hover:underline"
                  >
                    your Genie Page
                  </a>
                ) : (
                  "your Genie Page"
                )}
                {" "}— use a card&apos;s toggle to take one off.
              </p>
            </div>
          </div>

          {documents.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center">
              <GenieIcon name="files" size={72} color="#9CA3AF" className="mx-auto mb-5" />
              <h3 className="mb-2 text-xl font-semibold text-gray-900">No documents yet</h3>
              <p className="mx-auto mb-6 max-w-md text-gray-600">
                {sources.length === 0
                  ? "Add knowledge to this Genie first — a document is written from the sources it already holds."
                  : "Pick a format above and this Genie will write it from the sources you choose."}
              </p>
              {sources.length === 0 ? (
                <Button variant="outline" onClick={() => navigate(`/genie-hub/knowledge-base/${genie.id}`)}>
                  Go to sources
                </Button>
              ) : (
                <Button
                  onClick={() => openBuilder(null)}
                  className="bg-gradient-to-r from-genie-primary to-genie-secondary font-semibold text-white"
                >
                  Create a document
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {documents.map((entry) => (
                <SavedGenieDocumentCard
                  key={entry.row.id}
                  entry={entry}
                  brand={brand}
                  publicUrl={pageUrl ? `${pageUrl}/${entry.row.id}` : null}
                  publishBlockedReason={publishBlockedReason}
                  isPublishing={pendingId === entry.row.id}
                  onEdit={() => openForEdit(entry)}
                  onTogglePublished={(published) => void setPublished(entry.row, published)}
                  onDelete={() => setEntryToDelete(entry)}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default GenieDocumentsPage;
